import * as THREE from "three";
import { CharacterModel, type CharacterSpec } from "../render/CharacterModel";
import { moveWithCollision, type Circle, type SolidTest } from "./Collision";
import { ACTOR_RADIUS, ACTOR_SCALE, TILE, tileToWorld } from "./constants";
import { DIRECTION_ANGLES } from "./TileMap";
import type { Direction } from "../core/Input";

export interface ActorOptions {
  /** Spawn position, in tile coordinates. */
  x: number;
  y: number;
  facing?: Direction;
  appearance?: CharacterSpec;
  name?: string;
  /** Collision radius in world units. */
  radius?: number;
  /** Walk speed in world units per second. */
  speed?: number;
  /** Whether other actors collide with this one. */
  solid?: boolean;
}

/**
 * A character in the world. Position is continuous — actors are circles that
 * slide along walls — and the model turns smoothly towards wherever it is
 * heading.
 */
export class Actor {
  readonly object3D = new THREE.Group();
  readonly model: CharacterModel | null;

  name: string;
  solid: boolean;
  radius: number;
  speed: number;

  /** Facing, in radians about Y. 0 is +Z. */
  angle: number;
  private targetAngle: number;
  /** Distance covered this frame, used to drive the walk cycle. */
  private lastSpeed = 0;

  constructor(options: ActorOptions) {
    this.name = options.name ?? "";
    this.solid = options.solid ?? true;
    this.radius = options.radius ?? ACTOR_RADIUS;
    this.speed = options.speed ?? 3.6;

    this.model = options.appearance ? new CharacterModel(options.appearance) : null;
    if (this.model) {
      this.model.object3D.scale.multiplyScalar(ACTOR_SCALE);
      this.object3D.add(this.model.object3D);
    }

    this.angle = this.targetAngle = DIRECTION_ANGLES[options.facing ?? "down"];
    this.object3D.rotation.y = this.angle;
    this.object3D.position.set(tileToWorld(options.x), 0, tileToWorld(options.y));
  }

  get x(): number {
    return this.object3D.position.x;
  }

  get z(): number {
    return this.object3D.position.z;
  }

  /** Tile the actor is standing in. */
  get tileX(): number {
    return Math.floor((this.x + TILE / 2) / TILE);
  }

  get tileY(): number {
    return Math.floor((this.z + TILE / 2) / TILE);
  }

  get isMoving(): boolean {
    return this.lastSpeed > 0.05;
  }

  /** Height of the top of the head, for camera framing and popups. */
  get height(): number {
    return (this.model?.height ?? 1) * ACTOR_SCALE;
  }

  asCircle(): Circle {
    return { x: this.x, z: this.z, radius: this.radius };
  }

  setWorldPosition(x: number, z: number): void {
    this.object3D.position.set(x, 0, z);
  }

  setTile(tileX: number, tileY: number, facing?: Direction): void {
    this.setWorldPosition(tileToWorld(tileX), tileToWorld(tileY));
    if (facing) this.snapFacing(DIRECTION_ANGLES[facing]);
  }

  /** Turn towards an angle over the next few frames. */
  faceAngle(angle: number): void {
    this.targetAngle = angle;
  }

  faceTowards(x: number, z: number): void {
    this.faceAngle(Math.atan2(x - this.x, z - this.z));
  }

  snapFacing(angle: number): void {
    this.angle = this.targetAngle = angle;
    this.object3D.rotation.y = angle;
  }

  /**
   * Walks in a direction for one frame. `dirX`/`dirZ` should be a unit vector;
   * `intensity` scales it (a gamepad stick, or 1 for a key).
   */
  walk(
    dirX: number,
    dirZ: number,
    dt: number,
    isSolid: SolidTest,
    obstacles: readonly Circle[] = [],
    intensity = 1,
  ): boolean {
    const distance = this.speed * intensity * dt;
    const dx = dirX * distance;
    const dz = dirZ * distance;

    const before = { x: this.x, z: this.z };
    const next = moveWithCollision(before, dx, dz, this.radius, isSolid, obstacles);
    this.object3D.position.x = next.x;
    this.object3D.position.z = next.z;

    const moved = Math.hypot(next.x - before.x, next.z - before.z);
    this.lastSpeed = moved / Math.max(dt, 1e-5);

    // Face the way we are actually travelling, not the way we asked to.
    if (moved > 1e-4) this.faceAngle(Math.atan2(dx, dz));
    return moved > 1e-4;
  }

  /** Moves straight towards a point, ignoring input. Returns the distance left. */
  approach(
    x: number,
    z: number,
    dt: number,
    isSolid: SolidTest,
    obstacles: readonly Circle[] = [],
    speed = this.speed,
  ): number {
    const dx = x - this.x;
    const dz = z - this.z;
    const distance = Math.hypot(dx, dz);
    if (distance < 1e-4) {
      this.lastSpeed = 0;
      return 0;
    }
    const step = Math.min(distance, speed * dt);
    const before = { x: this.x, z: this.z };
    const next = moveWithCollision(before, (dx / distance) * step, (dz / distance) * step, this.radius, isSolid, obstacles);
    this.object3D.position.x = next.x;
    this.object3D.position.z = next.z;

    const moved = Math.hypot(next.x - before.x, next.z - before.z);
    this.lastSpeed = moved / Math.max(dt, 1e-5);
    if (moved > 1e-4) this.faceAngle(Math.atan2(dx, dz));
    return distance - moved;
  }

  /** Call once per frame, after any movement. */
  update(dt: number): void {
    // Shortest-way turn.
    let delta = this.targetAngle - this.angle;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    this.angle += delta * Math.min(1, dt * 12);
    this.object3D.rotation.y = this.angle;

    this.model?.setWalking(this.isMoving);
    this.model?.update(dt);

    // Decay so an actor that stopped being driven settles into idle.
    this.lastSpeed *= Math.max(0, 1 - dt * 12);
  }

  distanceTo(other: { x: number; z: number }): number {
    return Math.hypot(other.x - this.x, other.z - this.z);
  }

  dispose(): void {
    this.model?.dispose();
  }
}

export type NpcMovement = "fixed" | "wander" | "spin";

/** An actor with a little autonomy and something to say. */
export class NpcActor extends Actor {
  lines: string[];
  tag: string | undefined;
  turnsToPlayer: boolean;
  /** Held still while the player is talking to them. */
  frozen = false;

  private movement: NpcMovement;
  private homeX: number;
  private homeZ: number;
  private timer = 1 + Math.random() * 3;
  private target: { x: number; z: number } | null = null;

  constructor(
    options: ActorOptions & {
      movement?: NpcMovement;
      lines?: string[];
      tag?: string;
      turnsToPlayer?: boolean;
    },
  ) {
    super({ speed: 1.4, ...options });
    this.movement = options.movement ?? "fixed";
    this.lines = options.lines ?? [];
    this.tag = options.tag;
    this.turnsToPlayer = options.turnsToPlayer ?? true;
    this.homeX = this.x;
    this.homeZ = this.z;
  }

  tick(dt: number, isSolid: SolidTest, obstacles: readonly Circle[]): void {
    if (this.frozen || this.movement === "fixed") return;

    if (this.target) {
      const remaining = this.approach(this.target.x, this.target.z, dt, isSolid, obstacles);
      if (remaining < 0.12) this.target = null;
      return;
    }

    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = 2 + Math.random() * 3;

    const angle = Math.random() * Math.PI * 2;
    if (this.movement === "spin") {
      this.faceAngle(angle);
      return;
    }

    // Wanderers stay within a tile and a half of home, so they never drift
    // into a doorway and camp there.
    const reach = TILE * 1.2;
    this.target = {
      x: this.homeX + Math.sin(angle) * reach * Math.random(),
      z: this.homeZ + Math.cos(angle) * reach * Math.random(),
    };
  }
}

/**
 * A breadcrumb trail of where the leader has been. Followers walk to a point
 * a fixed arc-length back along it, which is what makes a party file through a
 * doorway one at a time instead of clumping.
 */
export class Trail {
  private points: { x: number; z: number; distance: number }[] = [];
  private total = 0;

  constructor(private spacing = 0.35) {}

  reset(x: number, z: number): void {
    this.points = [{ x, z, distance: 0 }];
    this.total = 0;
  }

  /** Records the leader's position; ignored until they have moved far enough. */
  push(x: number, z: number): void {
    const head = this.points[0];
    if (!head) {
      this.reset(x, z);
      return;
    }
    const step = Math.hypot(x - head.x, z - head.z);
    if (step < this.spacing) return;

    this.total += step;
    this.points.unshift({ x, z, distance: this.total });

    // Keep only what the longest party could possibly need.
    while (this.points.length > 256) this.points.pop();
  }

  /** Position `back` world units behind the head of the trail. */
  sample(back: number): { x: number; z: number } | null {
    const head = this.points[0];
    if (!head) return null;
    const wanted = this.total - back;
    if (wanted <= 0) return this.points[this.points.length - 1];

    for (let i = 1; i < this.points.length; i++) {
      const a = this.points[i - 1];
      const b = this.points[i];
      if (b.distance <= wanted && wanted <= a.distance) {
        const span = a.distance - b.distance || 1;
        const t = (wanted - b.distance) / span;
        return { x: b.x + (a.x - b.x) * t, z: b.z + (a.z - b.z) * t };
      }
    }
    return this.points[this.points.length - 1];
  }
}
